import React, { useState, useEffect } from 'react';
import { useAuthContext } from '../../providers/AuthProvider';
import { ThemeSidebarPanel } from '@textile-erp/ui';
import { Sidebar } from './Sidebar';
import { PageProvider } from '../../providers/PageContext';
import { UserGuideButton } from '../../components/UserGuideButton';

interface AppLayoutProps {
  children: React.ReactNode;
}

function AppLayoutInner({ children }: AppLayoutProps) {
  const { user } = useAuthContext();
  const [isThemeSettingsOpen, setIsThemeSettingsOpen] = useState(false);

  // Listen for theme settings open event from sidebar
  useEffect(() => {
    const handleOpenThemeSettings = () => {
      setIsThemeSettingsOpen(true);
    };

    window.addEventListener('openThemeSettings', handleOpenThemeSettings);
    return () => window.removeEventListener('openThemeSettings', handleOpenThemeSettings);
  }, []);

  if (!user) {
    return (
      <div className="min-h-screen bg-theme-secondary flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-theme-primary mb-4">Please Login</h1>
          <p className="text-theme-secondary">You need to be logged in to access this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-theme-primary flex overflow-hidden">
      {/* Sophisticated Dynamic Sidebar */}
      <Sidebar user={user} />
      
      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Main content area */}
        <main className="flex-1 overflow-hidden bg-theme-secondary flex flex-col">
            {children}
        </main>
      </div>

      {/* Theme Sidebar Panel */}
      <ThemeSidebarPanel
        isOpen={isThemeSettingsOpen}
        onClose={() => setIsThemeSettingsOpen(false)}
      />

      {/* Admin-only User Guide button (common to all pages) */}
      <UserGuideButton />
    </div>
  );
}

export function AppLayout({ children }: AppLayoutProps) {
  return (
    <PageProvider>
      <AppLayoutInner>{children}</AppLayoutInner>
    </PageProvider>
  );
}